import { useCallback, useEffect, useState } from 'react';
import {
  DeskPayment,
  deskPaymentApi,
  isDeskPaymentPending,
  useDeskPaymentQuery
} from '@/modules/billing';

type DeskPaymentFollowOptions = {
  pollingInterval: number;
  /** Stops the poll without forgetting the payment — the tracker's give-up deadline. */
  paused?: boolean;
};

/**
 * Follows one payment intent to its outcome. `POST /refresh` re-reads Stripe and advances the
 * state machine server-side; since 3c the resync engine's webhook handlers can do the same off
 * Stripe's own events, but the browser has no push channel of its own, so polling is still what
 * lets it learn of an outcome — webhook or poll, whichever lands first (see
 * `deskPayment.slice.ts`). Shared by the dialog's tracker and the headless
 * `DeskPaymentRefresher`, which differ only in what they render.
 *
 * What the outcome invalidates is dispatched from `deskPaymentRegistry.listener.ts`, on the
 * poll itself rather than from here — see its doc for why a follower cannot be trusted to carry
 * that, and for the transition the invalidation is tied to.
 *
 * The last known state is held here rather than read off the query, for two reasons: a status
 * that ends the poll also unsubscribes it, leaving the query with no `currentData` to fall back
 * on, and the state is monotonic — a payment that has reached its outcome never goes back to
 * what an in-flight refresh had to say about it.
 */
export const useDeskPaymentFollow = (
  startedPayment: DeskPayment,
  { pollingInterval, paused }: DeskPaymentFollowOptions
) => {
  const [payment, setPayment] = useState(startedPayment);
  const pending = isDeskPaymentPending(payment.status);

  const arg = { paymentIntentId: payment.paymentIntentId };
  const { isError } = useDeskPaymentQuery(arg, {
    skip: !pending || paused,
    // Re-subscribing — the tracker's "check again", or a refresher mounting on a payment
    // left running — re-reads at once instead of waiting an interval out.
    refetchOnMountOrArgChange: true,
    pollingInterval
  });
  // Read off the cache entry rather than off the subscription above: a paused follower (the
  // tracker past its deadline) polls nothing itself, but the registry's refresher follows the
  // same payment into that very entry, and the give-up screen must not keep offering to cancel
  // a payment that has since been captured.
  const { currentData } =
    deskPaymentApi.endpoints.deskPayment.useQueryState(arg);

  /** Records an outcome learnt elsewhere (the desk's own Cancel), under the same monotonic rule. */
  const reportOutcome = useCallback(
    (outcome: DeskPayment) =>
      setPayment((prev) =>
        isDeskPaymentPending(prev.status) ? outcome : prev
      ),
    []
  );

  useEffect(() => {
    if (currentData) {
      reportOutcome(currentData);
    }
  }, [currentData, reportOutcome]);

  return { payment, pending, isError, reportOutcome };
};
